import React, { useState, useRef } from 'react';
import { X, Send, Loader2, Mail, User, MessageSquare, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDashboard } from '../../../../context/dashboardContext';
import JoditEditor from "jodit-react";

const PDFModal = ({ pdfUrl, quoteData, customerData, quoteNumber, onClose, sawDetails, brochure: quoteBrochure = '', selectedAdditionalEmails = [], selectedContact = null }) => {
  const { emailQuotePdf } = useDashboard();
  const [isSending, setIsSending] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState('');
  // The Primary Contact picked on the quote screen is the intended recipient.
  // For the 'primary' entry that address IS customerData.email, but an
  // additional-location contact carries its own, so seeding from the customer
  // record alone addressed the wrong person.
  const contactEmail = (selectedContact?.email || '').trim();
  const customerEmail = (customerData?.email || '').trim();
  // Only offer the customer record's address separately when it isn't the
  // same one the contact row already shows.
  const showCustomerEmailRow = !selectedContact || !contactEmail ||
    customerEmail.toLowerCase() !== contactEmail.toLowerCase();
  // Initialize once at mount. The previous useEffect reset these on every
  // render whenever a parent passed an unstable prop reference (e.g. the
  // default `[]` for selectedAdditionalEmails on update-quote), which silently
  // wiped any recipients the user had just added via the "Add" button.
  const [selectedEmails, setSelectedEmails] = useState(() => {
    const arr = [];
    if (contactEmail) arr.push(contactEmail);
    else if (customerEmail) arr.push(customerEmail);
    if (Array.isArray(selectedAdditionalEmails)) arr.push(...selectedAdditionalEmails);
    return [...new Set(arr)];
  });
  const [emailSubject, setEmailSubject] = useState(() => `Quote #${quoteNumber} from BABS`);
  const [emailMessage, setEmailMessage] = useState(() => `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .signature { margin-top: 20px; }
          .signature strong { color: #dc2626; }
        </style>
      </head>
      <body>
        <p>Dear ${(selectedContact?.name || '').trim() || customerData?.customer || 'Valued Customer'},</p>

        <p>Please find attached your quote <strong>#${quoteNumber}</strong>.</p>

        <p>Thank you for your business!</p>

        <div class="signature">
          <p>Best regards,<br>
          <strong>BABS Team</strong></p>
        </div>
      </body>
      </html>
    `);

  const editor = useRef(null);

  // The brochure attached to the email is the QUOTE's brochure (per-quote), not the saw's.
  // No fallback: if the quote has no brochure, nothing is attached.
  const brochure = (quoteBrochure || '').trim();
  const isPdfBrochure = brochure && brochure.toLowerCase().endsWith('.pdf');

  const wrapInHtmlStructure = (content) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          body { 
            font-family: Arial, sans-serif; 
            line-height: 1.6; 
            color: #333;
            margin: 0;
            padding: 20px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
          }
          .header {
            border-bottom: 2px solid #dc2626;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            color: #666;
          }
          .signature {
            margin-top: 20px;
          }
          .signature strong {
            color: #dc2626;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2 style="color: #dc2626; margin: 0;">BABS Quote</h2>
          </div>
          
          ${content}
          
        </div>
      </body>
      </html>
    `;
  };

  const handleEmailToggle = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email) 
        : [...prev, email]
    );
  };

  const handleAddEmail = () => {
    const emails = additionalEmails.split(',')
      .map(email => email.trim())
      .filter(email => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValid && email) {
          toast.error(`Invalid email: ${email}`);
        }
        return isValid;
      });
    
    if (emails.length > 0) {
      setSelectedEmails(prev => [...new Set([...prev, ...emails])]);
      setAdditionalEmails('');
      toast.success(`${emails.length} email(s) added`);
    } else if (additionalEmails.trim()) {
      toast.error('Please enter valid email addresses separated by commas');
    }
  };

  const handleSendQuote = async () => {
    if (selectedEmails.length === 0) {
      toast.error('Please select at least one email address');
      return;
    }

    try {
      setIsSending(true);

      const completeHtmlMessage = wrapInHtmlStructure(emailMessage);

      const emailData = {
        to: selectedEmails,
        subject: emailSubject,
        message: completeHtmlMessage, // Send complete HTML
        clientName: customerData?.customer || 'Client',
        salesRepName: quoteData?.sales_rep_name || 'Sales Team',
        clientData: customerData,
        pdfUrl: pdfUrl,
        brochure: isPdfBrochure ? brochure : null
      };

      const response = await emailQuotePdf(quoteNumber, emailData);

      if (response.success) {
        toast.success(`Email Sent Successfully`);
        onClose();
      } else {
        toast.error(response.error || 'Failed to send quote');
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(error.message || 'Error sending quote. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Update Jodit config to allow full HTML
  const joditConfig = { readonly: false, height: 250, autofocus: false, allowTags: { p: true, br: true, strong: true, em: true, u: true, span: true, div: true, h1: true, h2: true, h3: true, ul: true, ol: true, li: true, table: true, tr: true, td: true, th: true, thead: true, tbody: true }, style: { fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: '1.6' } };

  const handleViewInNewTab = () => { window.open(pdfUrl, '_blank'); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[80vw] h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="flex justify-between items-center p-3 border-b bg-red-700 text-white">
          <div className='ml-3'>
            <h2 className="text-xl font-bold flex items-center gap-2"> <Mail className="h-5 w-5" /> Send Quote #{quoteNumber} </h2>
            {customerData && (
              <div className="text-sm mt-2 flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="font-medium">{customerData.customer || 'N/A'}</span>
                {selectedContact?.name
                  ? <span>• {selectedContact.name}</span>
                  : (customerData.title && <span>• {customerData.title}</span>)}
              </div>
            )}
          </div>

          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">

          {/* PDF Viewer */}
          <div className="flex-1 p-6 overflow-hidden max-w-[55%] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-700">Quote Preview</h3>
              <button onClick={handleViewInNewTab} className="flex items-center gap-1.5 px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs transition-colors" >
                <ExternalLink className="h-4 w-4" />
                Preview PDF Document
              </button>
            </div>
            
            <div className="h-full border-2 border-gray-200 rounded-lg shadow-inner overflow-auto flex-1">
              <iframe src={pdfUrl} className="w-full h-full min-h-[300px]" title="Quote PDF" />
            </div>
          </div>

          {/* Email Form */}
          <div className="w-[700px] border-l border-gray-200 flex flex-col">
            <div className="p-5 flex flex-col h-full">
              
              <h3 className="font-bold text-lg mb-5 text-gray-800 flex items-center gap-2"> <MessageSquare className="h-5 w-5 text-red-600 mt-1" /> Email Details </h3>

              <div className="flex-1 overflow-y-auto pr-2">
                <div className="space-y-4 min-h-0">

                  {/* Primary Contact — the recipient chosen on the quote screen */}
                  {selectedContact ? (
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700"> Primary Contact{selectedContact.name ? ` \u2014 ${selectedContact.name}` : ''} </label>
                    {contactEmail ? (
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <input type="checkbox" checked={selectedEmails.includes(contactEmail)} onChange={() => handleEmailToggle(contactEmail)} className="h-4 w-4 accent-red-600 cursor-pointer" />
                        <label className="ml-2 block text-sm text-gray-700 truncate"> {contactEmail} </label>
                      </div>
                    ) : (
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <span className="text-sm text-gray-500">No email on file for this contact</span>
                      </div>
                    )}
                  </div>
                ) : null}

                  {/* Customer Email */}
                  {customerData && showCustomerEmailRow ? (
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700"> Customer Email </label>
                    {customerEmail ? (
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <input type="checkbox" checked={selectedEmails.includes(customerEmail)} onChange={() => handleEmailToggle(customerEmail)} className="h-4 w-4 accent-red-600 cursor-pointer" />
                        <label className="ml-2 block text-sm text-gray-700 truncate"> {customerEmail} </label>
                      </div>
                    ) : (
                      <div className="flex items-center p-2 bg-gray-50 rounded-md">
                        <span className="text-sm text-gray-500">No email for selected customer is available</span>
                      </div>
                    )}
                  </div>
                ) : null}

                  {/* Customer Location - Additional Emails */}
                  {selectedAdditionalEmails && selectedAdditionalEmails.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700"> Customer Location - Additional Emails </label>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedAdditionalEmails.map((email, index) => (
                          <div key={index} className="flex items-center p-2 bg-gray-50 rounded-md">
                            <input type="checkbox" checked={selectedEmails.includes(email)} onChange={() => handleEmailToggle(email)} className="h-4 w-4 accent-red-600 cursor-pointer" />
                            <label className="ml-2 block text-sm text-gray-700 truncate"> {email} </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Emails */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700"> Additional Recipients </label>

                    <div className="flex rounded-md shadow-sm">
                      <input
                        type="text"
                        value={additionalEmails}
                        onChange={(e) => setAdditionalEmails(e.target.value)}
                        placeholder="email@example.com, another@email.com"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-r-md text-sm"
                      />
                      <button onClick={handleAddEmail} className="px-3 py-1.5 bg-red-600 text-white rounded-r-md hover:bg-red-700 text-sm" > Add </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1"> Separate multiple emails with commas </p>
                  </div>

                  {/* Selected Emails */}
                  {selectedEmails.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700"> Selected Recipients ({selectedEmails.length}) </label>
                      <div className="bg-gray-50 p-2 rounded-md border max-h-28 overflow-y-auto">
                        <div className="flex flex-wrap gap-1.5">
                          {selectedEmails.map((email, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" >
                              {email}
                              <button onClick={() => handleEmailToggle(email)} className="ml-1 text-red-500 hover:text-red-700" > <X className="h-2.5 w-2.5" /> </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Brochure Attachment — this quote's brochure */}
                  {brochure && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700"> Attachments </label>
                      {isPdfBrochure ? (
                        <div className="bg-green-50 p-2 rounded-md border border-green-200">
                          <div className="flex items-center text-sm text-green-800">
                            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Brochure will be attached</span>
                          </div>
                          <div className="text-xs text-green-600 mt-1 ml-5.5"> <strong>Brochure:</strong> {brochure} </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 p-2 rounded-md border border-orange-200">
                          <div className="flex items-center text-sm text-yellow-800">
                            <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span>No brochure selected for this quote</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Subject */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700"> Subject </label>
                    <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm" />
                  </div>

                  {/* Message (Jodit Editor) */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700"> Message </label>
                    <JoditEditor key="stable-editor" ref={editor} value={emailMessage} onBlur={(content) => setEmailMessage(content)} config={joditConfig} />
                  </div>

                </div>
              </div>

              {/* Send Button */}
              <div className="mt-4">
                <button
                  onClick={handleSendQuote}
                  disabled={isSending || selectedEmails.length === 0}
                  className={`w-full py-2 px-4 rounded-md text-white font-medium flex items-center justify-center transition-colors ${
                    isSending || selectedEmails.length === 0
                      ? 'bg-red-300 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {isSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Quote
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PDFModal;
